/**
 * events.ts — random timed events that push a modifier onto the buff stack.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { next } from '../core/rng';
import { BALANCE, EVENTS, EVENT_BY_ID, type EventDef } from '../data';
import { meetsRequirement } from './unlock';
import { addModifier } from './modifiers';

function eligibleEvents(state: GameState): EventDef[] {
  return EVENTS.filter((e) => meetsRequirement(state, e.requires));
}

function weightedPick(state: GameState, pool: EventDef[]): EventDef | null {
  if (pool.length === 0) return null;
  const total = pool.reduce((sum, e) => sum + e.weight, 0);
  let roll = next(state.rng) * total;
  for (const e of pool) {
    roll -= e.weight;
    if (roll <= 0) return e;
  }
  return pool[pool.length - 1] ?? null;
}

export function tickEvents(state: GameState, bus?: EventBus): void {
  if (state.stats.lifetimeEarnings < BALANCE.events.unlockLifetime) return;
  const now = state.meta.gameTimeMs;

  // Detect an active event whose modifier has expired.
  if (state.events.activeId) {
    const stillActive = state.modifiers.some((m) => m.id === `event:${state.events.activeId}`);
    if (!stillActive) {
      bus?.emit('event:ended', { eventId: state.events.activeId });
      state.events.activeId = null;
      state.events.cooldownUntil = now + BALANCE.events.cooldown * 1000;
    }
    return;
  }

  if (now < state.events.cooldownUntil) return;
  if (now < state.events.nextRollAt) return;
  state.events.nextRollAt = now + BALANCE.events.rollInterval * 1000;

  if (next(state.rng) >= BALANCE.events.chance) return;

  const event = weightedPick(state, eligibleEvents(state));
  if (!event) return;

  addModifier(state, {
    id: `event:${event.id}`,
    label: event.name,
    effect: event.effect,
    endsAt: now + event.duration * 1000,
  });
  state.events.activeId = event.id;
  bus?.emit('event:started', { eventId: event.id, name: event.name, description: event.description });
  bus?.emit('notify', {
    kind: event.kind === 'good' ? 'reward' : 'warning',
    message: `${event.name}: ${event.description}`,
  });
}

/** Human-readable remaining time (ms) on the active event, or 0. */
export function activeEventRemaining(state: GameState): number {
  if (!state.events.activeId) return 0;
  const mod = state.modifiers.find((m) => m.id === `event:${state.events.activeId}`);
  return mod ? Math.max(0, mod.endsAt - state.meta.gameTimeMs) : 0;
}

export function activeEvent(state: GameState): EventDef | null {
  return state.events.activeId ? EVENT_BY_ID[state.events.activeId] ?? null : null;
}
